import { Link, useLocation } from 'react-router-dom';
import { useFrappeAuth, useFrappeGetCall } from 'frappe-react-sdk';
import toast from 'react-hot-toast';
import { LogOut, Activity, BarChart3, BookOpen, Bell, User, Calculator } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

// Helper function to get the correct login URL based on environment
const getLoginUrl = () => {
  const isDev = import.meta.env.DEV;
  return isDev ? '/assets/surgical_training/frontend/login' : '/isim/login';
};

interface NavbarProps {
  currentPage?: string;
}

const Navbar = ({ currentPage }: NavbarProps) => {
  const { currentUser, logout } = useFrappeAuth();
  const [showNotifications, setShowNotifications] = useState(false);
  const [hasNotifications] = useState(false);
  const notificationRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  // Get user's full name from User document
  const { data: userData } = useFrappeGetCall(
    'frappe.client.get',
    {
      doctype: 'User',
      name: currentUser
    },
    undefined,
    {
      isPaused: () => !currentUser
    }
  );

  const handleLogout = async () => {
    try {
      sessionStorage.removeItem('surgical_training_onboarding_session');
      await logout();
      toast.success('Logged out successfully');
      window.location.replace(getLoginUrl());
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
      window.location.replace(getLoginUrl());
    }
  };

  const navItems = [
    { name: 'Dashboard', path: '/dashboard', icon: Activity, key: 'dashboard' },
    { name: 'Guide', path: '/guide', icon: BookOpen, key: 'guide' },
    { name: 'Analytics', path: '/analytics', icon: BarChart3, key: 'analytics' },
  ];

  // Close notifications on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setShowNotifications(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="sticky top-0 z-50 w-full bg-white/90 backdrop-blur-lg border-b border-gray-200 shadow-sm supports-[backdrop-filter]:bg-white/60">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Left side - Logo + Nav Items */}
          <div className="flex items-center gap-8">
            <Link
              to="/dashboard"
              className="flex items-center gap-2.5 group"
            >
              <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center shadow-lg shadow-blue-600/20 transition-transform group-hover:scale-105">
                <Activity size={20} className="text-white" />
              </div>
              <span className="text-gray-900 font-bold text-lg hidden sm:block tracking-tight group-hover:text-blue-600 transition-colors">
                Surgical Training
              </span>
            </Link>

            <nav className="hidden md:flex items-center gap-1">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <Link
                    key={item.key}
                    to={item.path}
                    className={`
                      flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200
                      ${isActive
                        ? 'bg-blue-600 text-white shadow-sm'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }
                    `}
                  >
                    <Icon size={16} className={isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-600'} />
                    <span>{item.name}</span>
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Right side - User Cluster */}
          <div className="flex items-center gap-2">

            {/* Notifications */}
            <div className="relative" ref={notificationRef}>
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className={`
                  p-2 rounded-full transition-colors relative
                  ${showNotifications ? 'bg-blue-50 text-blue-600' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}
                `}
              >
                <Bell size={20} />
                {hasNotifications && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full border-2 border-white" />
                )}
              </button>

              {/* Notification Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-100 py-2 animate-in fade-in zoom-in-95 duration-200 origin-top-right ring-1 ring-black/5 focus:outline-none">
                  <div className="px-4 py-2 border-b border-gray-50 flex justify-between items-center">
                    <span className="font-semibold text-gray-900 text-sm">Notifications</span>
                  </div>
                  <div className="p-4">
                    {hasNotifications ? (
                      <div className="space-y-3">
                        <div className="flex items-start gap-3 p-3 bg-blue-50/50 rounded-lg">
                          <div className="w-1.5 h-1.5 bg-blue-500 rounded-full mt-2" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">New session assigned</p>
                            <p className="text-xs text-gray-500 mt-1">2m ago</p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-center py-6 text-gray-500">
                        <Bell size={24} className="mx-auto mb-2 text-gray-300" />
                        <p className="text-sm">No new notifications</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="h-6 w-px bg-gray-200 mx-2" />

            {/* Profile */}
            <Link
              to="/account"
              className="flex items-center gap-3 pl-1 pr-2 py-1.5 rounded-full hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-200"
            >
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                {(userData?.full_name || currentUser || 'U').charAt(0).toUpperCase()}
              </div>
              <div className="hidden sm:block text-left">
                <p className="text-xs font-medium text-gray-900 leading-none mb-1">
                  {userData?.full_name || currentUser}
                </p>
                <p className="text-[10px] text-gray-500 leading-none">View Profile</p>
              </div>
            </Link>

            {/* Logout */}
            <button
              onClick={handleLogout}
              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-all"
              title="Logout"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;